/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { bool, object } from 'prop-types';
import mbpUtil from '@api-util/mbp';
import ConversationalUIChatBot from './ConversationalUIChatBot';
import { CHAT_EXPERIENCES, isExperienceEnabled, isLauncherVisible } from './chatExperiences';
import { OPEN_CHAT_EVENT } from './openChat';
import getTrustedOrigin from './helper/TrustedOrigin';

const ConversationalChatHost = ({ customerServiceEnabled, featureFlags, location, ...frameProps }) => {
    const [activeExperience, setActiveExperience] = useState(null);
    const [triggers, setTriggers] = useState({});
    // In-memory only: opened chats keep their launcher until ended or the page reloads.
    const [persistChat, setPersistChat] = useState({});
    const [scrollLocks, setScrollLocks] = useState({});
    const activeRef = useRef(null);
    const trustedOrigin = getTrustedOrigin(mbpUtil.getEnv('APP_CONVERSATIONAL_UI_HOST'));
    const enabled = Object.keys(CHAT_EXPERIENCES).filter((id) => isExperienceEnabled(id, featureFlags, customerServiceEnabled));
    const activate = useCallback((id) => {
        if (id === 'customerService') setPersistChat((previous) => ({ ...previous, [id]: true }));
        activeRef.current = id;
        setActiveExperience(id);
    }, []);
    const minimize = useCallback((id) => {
        if (activeRef.current !== id) return;
        activeRef.current = null;
        setActiveExperience(null);
    }, []);
    const endChat = useCallback((id) => {
        if (id === 'customerService') setPersistChat((previous) => ({ ...previous, [id]: false }));
        minimize(id);
    }, [minimize]);
    const onScrollLock = useCallback((id, locked) => {
        setScrollLocks((previous) => (previous[id] === locked ? previous : { ...previous, [id]: locked }));
    }, []);

    useEffect(() => {
        const open = (id, message) => {
            if (!enabled.includes(id)) return;
            activate(id);
            // The frame retains the request until its session handshake completes.
            setTriggers((previous) => ({ ...previous, [id]: { message } }));
        };
        const handleOpen = (event) => {
            const { experience = 'customerService', source } = event.detail || {};
            open(experience, source === 'header'
                ? { type: 'chat-header-button', data: { clicked: true } }
                : { type: 'trigger-flora-chat' });
        };
        const handleSuggestion = (event) => {
            if (event.origin !== trustedOrigin) return;
            const suggestions = {
                'flora-search-suggestion-submitted': ['18fchatbotsearch', 'flora-update-chat-message-search'],
                'flora-footer-suggestion-submitted': ['18fchatbotfooter', 'flora-update-chat-message-footer'],
            };
            const target = suggestions[event.data?.type];
            if (!target || event.source !== document.getElementById(target[0])?.contentWindow) return;
            open('customerService', { type: target[1], data: event.data });
        };
        window.addEventListener(OPEN_CHAT_EVENT, handleOpen);
        window.addEventListener('message', handleSuggestion);
        return () => {
            window.removeEventListener(OPEN_CHAT_EVENT, handleOpen);
            window.removeEventListener('message', handleSuggestion);
        };
    }, [featureFlags, customerServiceEnabled, trustedOrigin, activate]);

    useEffect(() => {
        if (activeExperience && !enabled.includes(activeExperience)) minimize(activeExperience);
    }, [activeExperience, featureFlags, customerServiceEnabled, minimize]);

    const lockScroll = Boolean(activeExperience && enabled.includes(activeExperience) && scrollLocks[activeExperience]);
    useEffect(() => {
        if (!lockScroll) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [lockScroll]);

    return enabled.map((id, index) => (
        <ConversationalUIChatBot
            // Stable identity and src preserve each conversation across switches.
            key={id}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...frameProps}
            featureFlags={featureFlags}
            location={location}
            experienceId={id}
            chatRoute={CHAT_EXPERIENCES[id].route}
            active={activeExperience === id}
            launcherVisible={!activeExperience && (Boolean(persistChat[id]) || isLauncherVisible(id, featureFlags, location?.pathname, frameProps.pageType))}
            launcherOffset={10 + index * 160}
            trigger={triggers[id]}
            onActivate={activate}
            onMinimize={minimize}
            onEndChat={endChat}
            onScrollLock={onScrollLock}
        />
    ));
};

ConversationalChatHost.propTypes = {
    customerServiceEnabled: bool.isRequired,
    featureFlags: object.isRequired,
    location: object.isRequired,
};

export default ConversationalChatHost;
